import { useCallback, useEffect, useState } from "react";
import { Trash2, Receipt, Banknote, CreditCard, Landmark, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, euro, prezzoFinale, scontoLabel, formatError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { SearchSelect } from "@/components/SearchSelect";
import { ConfirmDelete } from "@/components/ConfirmDelete";

export const METODI = [
  { value: "contanti", label: "Contanti", icon: Banknote },
  { value: "carta", label: "Carta", icon: CreditCard },
  { value: "bancomat", label: "Bancomat", icon: Wallet },
  { value: "bonifico", label: "Bonifico", icon: Landmark },
];
const today = () => new Date().toISOString().slice(0, 10);
const L = ({ children }) => <Label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">{children}</Label>;

const Carrello = ({ righe, setRighe }) => {
  const upd = (i, k, v) => setRighe(righe.map((r, j) => (j === i ? { ...r, [k]: v === "" ? "" : Number(v) } : r)));
  if (!righe.length) return <p className="rounded-md border border-dashed border-slate-200 py-10 text-center text-sm text-slate-500" data-testid="cassa-carrello-vuoto">Aggiungi un articolo per iniziare la vendita.</p>;
  return (
    <div className="space-y-2" data-testid="cassa-carrello">
      {righe.map((r, i) => (
        <div key={`${r.tipo}-${r.articolo_id}`} className="grid grid-cols-[1fr_70px_100px_90px_36px] items-center gap-2 rounded-md border border-slate-100 bg-slate-50/60 p-2" data-testid={`cassa-riga-${i}`}>
          <div className="min-w-0"><p className="truncate text-sm font-medium">{r.nome}</p><p className="text-[11px] text-slate-400">{r.tipo === "prodotto" ? "Prodotto" : "Ricambio"} · disp. {r.disp}</p></div>
          <Input type="number" min="1" value={r.quantita} onChange={(e) => upd(i, "quantita", e.target.value)} className="bg-white" data-testid={`cassa-riga-qty-${i}`} />
          <Input type="number" step="0.01" value={r.prezzo} onChange={(e) => upd(i, "prezzo", e.target.value)} className="bg-white" data-testid={`cassa-riga-price-${i}`} />
          <p className="text-right font-mono text-sm font-bold">{euro((r.quantita || 0) * (r.prezzo || 0))}</p>
          <Button variant="ghost" size="icon" onClick={() => setRighe(righe.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600" data-testid={`cassa-riga-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
    </div>
  );
};

const Registro = ({ reg, giorno, setGiorno, onAnnulla }) => (
  <section className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="cassa-registro">
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
      <h2 className="font-heading text-base font-bold">Registro vendite</h2>
      <Input type="date" value={giorno} onChange={(e) => setGiorno(e.target.value)} className="w-44" data-testid="cassa-registro-data-input" />
    </div>
    <div className="grid grid-cols-2 gap-px border-b border-slate-100 bg-slate-100 sm:grid-cols-5">
      <div className="bg-slate-900 p-4 text-white"><p className="text-[10px] font-bold uppercase tracking-wider text-orange-400">Totale giorno</p><p className="font-mono text-xl font-bold" data-testid="cassa-totale-giorno">{euro(reg?.totale)}</p></div>
      {METODI.map((m) => (
        <div key={m.value} className="bg-white p-4"><p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{m.label}</p><p className="font-mono text-base font-bold" data-testid={`cassa-totale-${m.value}`}>{euro(reg?.per_metodo?.[m.value])}</p></div>
      ))}
    </div>
    <ul className="divide-y divide-slate-100">
      {reg?.vendite.map((v) => (
        <li key={v.id} className="flex items-center gap-3 px-5 py-3 text-sm" data-testid={`cassa-vendita-${v.id}`}>
          <div className="flex-1">
            <p><span className="font-mono text-xs font-bold text-orange-600">{v.numero}</span> <span className="text-xs text-slate-500">· {new Date(v.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })} · {METODI.find((m) => m.value === v.metodo_pagamento)?.label} · {v.operatore}</span></p>
            <p className="text-slate-700">{v.righe.map((r) => `${r.quantita}× ${r.nome}`).join(", ")}</p>
          </div>
          <span className="font-mono font-bold">{euro(v.totale)}</span>
          <ConfirmDelete testId={`cassa-annulla-${v.id}`} onConfirm={() => onAnnulla(v.id)} label="Annulla vendita" description="La vendita verrà annullata e gli articoli torneranno in magazzino." />
        </li>
      ))}
      {reg && !reg.vendite.length && <li className="px-5 py-10 text-center text-sm text-slate-500">Nessuna vendita in questa data.</li>}
    </ul>
  </section>
);

export default function Cassa() {
  const [articoli, setArticoli] = useState([]);
  const [righe, setRighe] = useState([]);
  const [metodo, setMetodo] = useState("contanti");
  const [note, setNote] = useState("");
  const [giorno, setGiorno] = useState(today());
  const [reg, setReg] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadArticoli = useCallback(async () => {
    const [p, r] = await Promise.all([api.get("/prodotti"), api.get("/ricambi")]);
    setArticoli([...p.data.map((x) => ({ ...x, tipo: "prodotto" })), ...r.data.map((x) => ({ ...x, tipo: "ricambio" }))]);
  }, []);
  const loadReg = useCallback(() => api.get("/vendite", { params: { data: giorno } }).then((r) => setReg(r.data)), [giorno]);
  useEffect(() => { loadArticoli(); }, [loadArticoli]);
  useEffect(() => { loadReg(); }, [loadReg]);

  const opts = articoli.map((a) => ({ value: `${a.tipo}:${a.id}`, label: `${a.nome}${a.codice ? ` (${a.codice})` : ""}`, hint: `${a.tipo === "prodotto" ? "Prod." : "Ric."} · disp. ${a.quantita} · ${euro(prezzoFinale(a))}${a.sconto ? ` (${scontoLabel(a)})` : ""}` }));
  const add = (key) => {
    const [tipo, id] = key.split(":");
    const a = articoli.find((x) => x.id === id && x.tipo === tipo);
    const i = righe.findIndex((r) => r.articolo_id === id && r.tipo === tipo);
    if (i >= 0) return setRighe(righe.map((r, j) => (j === i ? { ...r, quantita: Number(r.quantita) + 1 } : r)));
    setRighe([...righe, { tipo, articolo_id: id, nome: a.nome, codice: a.codice, quantita: 1, prezzo: prezzoFinale(a), disp: a.quantita }]);
  };
  const totale = righe.reduce((s, r) => s + (Number(r.quantita) || 0) * (Number(r.prezzo) || 0), 0);

  const registra = async () => {
    if (!righe.length) return toast.error("Il carrello è vuoto");
    setSaving(true);
    try {
      const payload = { metodo_pagamento: metodo, note, righe: righe.map(({ disp, ...r }) => ({ ...r, quantita: Number(r.quantita) || 1, prezzo: Number(r.prezzo) || 0 })) };
      const { data } = await api.post("/vendite", payload);
      toast.success(`Vendita ${data.numero} registrata: ${euro(data.totale)}`);
      setRighe([]); setNote("");
      setGiorno(today());
      loadReg(); loadArticoli();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const annulla = async (id) => {
    try { await api.delete(`/vendite/${id}`); toast.success("Vendita annullata"); loadReg(); loadArticoli(); } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div>
      <PageHeader overline="Negozio" title="Vendita al banco" />
      <div className="mb-6 grid gap-6 xl:grid-cols-[1fr_340px]">
        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <L>Aggiungi articolo (prodotti e ricambi)</L>
          <div className="mb-4"><SearchSelect options={opts} value="" onChange={add} placeholder="Cerca per nome o codice..." testId="cassa-add-articolo" emptyText="Nessun articolo trovato" /></div>
          <Carrello righe={righe} setRighe={setRighe} />
        </section>
        <aside className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <L>Metodo di pagamento</L>
            <div className="grid grid-cols-2 gap-2">
              {METODI.map(({ value, label, icon: Icon }) => (
                <button key={value} onClick={() => setMetodo(value)} data-testid={`cassa-metodo-${value}`}
                  className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2.5 text-sm font-semibold transition-colors ${metodo === value ? "border-orange-600 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                  <Icon className="h-4 w-4" /> {label}
                </button>
              ))}
            </div>
            <div className="mt-4"><L>Note</L><Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="cassa-note-input" /></div>
          </div>
          <div className="rounded-lg bg-slate-900 p-5 text-white">
            <div className="flex items-end justify-between"><span className="text-xs font-bold uppercase tracking-wider text-orange-400">Totale</span><span className="font-mono text-3xl font-bold" data-testid="cassa-totale">{euro(totale)}</span></div>
            <Button onClick={registra} disabled={saving || !righe.length} className="mt-4 h-11 w-full bg-orange-600 hover:bg-orange-700" data-testid="cassa-registra-button">
              <Receipt className="mr-1 h-4 w-4" /> {saving ? "Registrazione..." : "Registra vendita"}
            </Button>
            <p className="mt-2 text-[11px] text-slate-400">Prezzi IVA inclusa. Gli articoli vengono scaricati dal magazzino.</p>
          </div>
        </aside>
      </div>
      <Registro reg={reg} giorno={giorno} setGiorno={setGiorno} onAnnulla={annulla} />
    </div>
  );
}
